// frontend/src/pages/StudentArticleReader.js — NEW FILE (Phase C)
// Full-page reader for one card, Perplexity-article style:
// - Renders all blocks of the opened card
// - Continuous scroll: near the bottom, an "Up next" divider appears and the
//   next card's blocks load inline (prefetched in background for instant feel)
// - Fullscreen media viewer with pinch zoom, double-tap zoom, pan, and the
//   caption/explanation shown INSIDE the viewer (no phone rotation needed)
// - Hardware back closes fullscreen first, then the reader (handled by feed)

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { API_BASE, authFetch } from '../services/api.js';
import ContentBlockRenderer, { stripHtml, resolveImageUrl } from './ContentBlockRenderer';
import AskAiPanel, { getStudentId } from './AskAiPanel';
import './studentfeed.css';
import { reportProgress, ConfidenceStrip, FlagChip } from './ProgressWidgets';
import { useLanguage } from '../contexts/languagecontext';



const LABELS = {
    en: { upNext: 'Up next', inThisSection: 'In this section', studyThis: 'Study this', minRead: 'min read', cardOf: (a, b) => `${a} of ${b}`, loading: 'Loading...', end: "You've completed this topic 🎉", loadFail: 'Could not load. Tap to retry.' },
    hi: { upNext: 'आगे', inThisSection: 'इस भाग में', studyThis: 'अध्ययन करें', minRead: 'min read', cardOf: (a, b) => `${a} / ${b}`, loading: 'लोड हो रहा है...', end: 'आपने यह टॉपिक पूरा कर लिया 🎉', loadFail: 'लोड नहीं हो सका। दोबारा कोशिश के लिए टैप करें।' },
};


// ─────────────────────────────────────────────────────────────────────────────
// Pinch-zoom fullscreen viewer (images) / plain fullscreen (video)
// ─────────────────────────────────────────────────────────────────────────────
const FullscreenViewer = ({ media, onClose }) => {
    const [scale, setScale] = useState(1);
    const [tx, setTx] = useState(0);
    const [ty, setTy] = useState(0);
    const gesture = useRef({ pointers: new Map(), startDist: 0, startScale: 1, startTx: 0, startTy: 0, startMidX: 0, startMidY: 0, lastTap: 0 });

    const clamp = (v, min, max) => Math.min(max, Math.max(min, v));

    const onPointerDown = (e) => {
        const g = gesture.current;
        g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (g.pointers.size === 2) {
            const pts = [...g.pointers.values()];
            g.startDist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            g.startScale = scale;
            g.startMidX = (pts[0].x + pts[1].x) / 2;
            g.startMidY = (pts[0].y + pts[1].y) / 2;
            g.startTx = tx;
            g.startTy = ty;
        } else if (g.pointers.size === 1) {
            g.startTx = tx;
            g.startTy = ty;
            g.startMidX = e.clientX;
            g.startMidY = e.clientY;
            // double-tap
            const now = Date.now();
            if (now - g.lastTap < 300) {
                if (scale > 1) { setScale(1); setTx(0); setTy(0); }
                else setScale(2.5);
            }
            g.lastTap = now;
        }
    };

    const onPointerMove = (e) => {
        const g = gesture.current;
        if (!g.pointers.has(e.pointerId)) return;
        g.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
        if (g.pointers.size === 2) {
            const pts = [...g.pointers.values()];
            const dist = Math.hypot(pts[0].x - pts[1].x, pts[0].y - pts[1].y);
            setScale(clamp(g.startScale * (dist / (g.startDist || 1)), 1, 4));
        } else if (g.pointers.size === 1 && scale > 1) {
            setTx(g.startTx + (e.clientX - g.startMidX));
            setTy(g.startTy + (e.clientY - g.startMidY));
        }
    };

    const onPointerUp = (e) => {
        const g = gesture.current;
        g.pointers.delete(e.pointerId);
        if (scale <= 1.02) { setScale(1); setTx(0); setTy(0); }
    };

    return (
        <div className="star-fullscreen">
            <button className="star-fs-close" onClick={onClose} aria-label="Close">&larr;</button>
            <div
                className="star-fs-stage"
                onPointerDown={onPointerDown}
                onPointerMove={onPointerMove}
                onPointerUp={onPointerUp}
                onPointerCancel={onPointerUp}
                style={{ touchAction: 'none' }}
            >
                {media.type === 'image' ? (
                    <img
                        src={media.url}
                        alt={media.caption || ''}
                        className="star-fs-img"
                        draggable={false}
                        style={{ transform: `translate(${tx}px, ${ty}px) scale(${scale})` }}
                    />
                ) : (
                    <div className="star-fs-video">
                        <iframe src={media.url} title="video" allowFullScreen />
                    </div>
                )}
            </div>
            {media.caption && scale <= 1.02 && (
                <div className="star-fs-caption">{media.caption}</div>
            )}
        </div>
    );
};

// ─────────────────────────────────────────────────────────────────────────────
// Reader — single card at a time; drills into chunks / micro-chunks.
// One nav stack + one popstate handler (card levels + fullscreen as entries).
// Shared cache + child prefetch preserve the instant feel; back restores scroll.
// ─────────────────────────────────────────────────────────────────────────────
const GRADIENTS = [
    ['#4f46e5', '#7c3aed'],
    ['#0e7490', '#059669'],
    ['#b45309', '#dc2626'],
    ['#be185d', '#7c3aed'],
    ['#1d4ed8', '#0e7490'],
];
const gradientFor = (text) => {
    let h = 0;
    const s = text || '';
    for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) % 997;
    return GRADIENTS[h % GRADIENTS.length];
};

const mergeContent = (blocks, children) => {
    const bItems = (Array.isArray(blocks) ? blocks : []).map((b, i) => ({ kind: 'block', order_index: (b.order_index ?? (i + 1)), key: i, data: b }));
    const cItems = (Array.isArray(children) ? children : []).map((c) => ({ kind: 'child', order_index: (c.order_index ?? 1e9), data: c }));
    return [...bItems, ...cItems].sort((a, b) => a.order_index - b.order_index);
};

const childEndpoint = (c) => {
    if (c.card_type === 'subtopic') return `/content-page/chunks/${c.card_id}`;
    if (c.card_type === 'chunk') return `/content-page/micro-chunks/${c.card_id}`;
    return null;
};

const StudentArticleReader = ({ topicId, card, language = 'en', topicName, onClose }) => {
    const t = LABELS[language] || LABELS.en;
    const { hideImages, toggleImages } = useLanguage();

    const cache = useRef({});            // `${type}-${id}` → blocks
    const childrenCache = useRef({});    // `${type}-${id}` → child cards
    const savedScrolls = useRef({});     // depth → scrollY

    // Nav stack: entries are { kind: 'card', card } or { kind: 'fs', media }
    const [stack, setStack] = useState([{ kind: 'card', card }]);
    const stackRef = useRef(stack);
    useEffect(() => { stackRef.current = stack; }, [stack]);

    const [blocks, setBlocks] = useState(null);        // null=loading, []=none, [...]
    const [blocksError, setBlocksError] = useState(false);
    const [children, setChildren] = useState(null);
    const [retryTick, setRetryTick] = useState(0);
    const [expandedChildIds, setExpandedChildIds] = useState({});
    const [progressMap, setProgressMap] = useState({});
    useEffect(() => {
        const sid = getStudentId();
        if (!sid) return;
        authFetch(`${API_BASE}/progress/topic/${topicId}?student_id=${sid}`)
            .then((r) => r.ok ? r.json() : null)
            .then((d) => { if (d) setProgressMap(d); })
            .catch(() => {});
    }, [topicId]);

    // ── Ask AI (admin-configurable; hidden unless enabled) ──
    const [askAiConf, setAskAiConf] = useState(null);
    const [askAiTarget, setAskAiTarget] = useState(null);
    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const sid = getStudentId();
                const res = await fetch(`${API_BASE}/ask-ai/config/topic/${topicId}${sid ? `?student_id=${sid}` : ''}`);
                if (res.ok) { const d = await res.json(); if (!cancelled) setAskAiConf(d); }
            } catch { /* feature stays hidden on failure */ }
        })();
        return () => { cancelled = true; };
    }, [topicId]);

    const fetchBlocks = useCallback(async (c) => {
        const key = `${c.card_type}-${c.card_id}`;
        if (cache.current[key]) return cache.current[key];
        const res = await fetch(`${API_BASE}/content-page/card/${c.card_type}/${c.card_id}?language=${language}`);
        if (!res.ok) throw new Error('load-failed');
        const data = await res.json();
        cache.current[key] = data.blocks || [];
        return cache.current[key];
    }, [language]);

    const loadChildren = useCallback(async (c) => {
        const key = `${c.card_type}-${c.card_id}`;
        if (childrenCache.current[key]) return childrenCache.current[key];
        const ep = childEndpoint(c);
        if (!ep) { childrenCache.current[key] = []; return []; }
        try {
            const res = await fetch(`${API_BASE}${ep}?language=${language}`);
            const list = res.ok ? await res.json() : [];
            childrenCache.current[key] = list;
            return list;
        } catch { childrenCache.current[key] = []; return []; }
    }, [language]);

    // Active card = the deepest 'card' entry in the stack
    const cardEntries = stack.filter((e) => e.kind === 'card');
    const activeCard = cardEntries[cardEntries.length - 1].card;
    const parentCard = cardEntries.length >= 2 ? cardEntries[cardEntries.length - 2].card : null;
    const activeKey = `${activeCard.card_type}-${activeCard.card_id}`;

    // Load blocks + children whenever the active card changes
    useEffect(() => {
        let cancelled = false;

        if (cache.current[activeKey]) {
            setBlocks(cache.current[activeKey]);
        } else {
            setBlocks(null); setBlocksError(false);
            fetchBlocks(activeCard)
                .then((b) => { if (!cancelled) { setBlocks(b); reportProgress(topicId, activeCard, { status: 'viewed' }); } })
                .catch(() => { if (!cancelled) setBlocksError(true); });
        }

        if (childrenCache.current[activeKey]) {
            setChildren(childrenCache.current[activeKey]);
        } else {
            setChildren(null);
            loadChildren(activeCard).then((list) => {
                if (cancelled) return;
                setChildren(list);
                list.forEach((ch) => fetchBlocks(ch).catch(() => {}));  // prefetch → instant open
            });
        }

        return () => { cancelled = true; };
    }, [activeKey, retryTick, activeCard, fetchBlocks, loadChildren, topicId]);

    const retry = () => { delete cache.current[activeKey]; setBlocksError(false); setBlocks(null); setRetryTick((x) => x + 1); };

    // ── Single back handler for the whole reader (card levels + fullscreen) ──
    useEffect(() => {
        const onPop = () => {
            const prev = stackRef.current;
            const top = prev[prev.length - 1];
            if (top.kind === 'card') reportProgress(topicId, top.card, { status: 'completed' });
            const next = prev.slice(0, -1);
            if (next.length === 0) { onClose(); return; }   // base popped → close reader
            setStack(next);
            const depth = next.length - 1;
            const y = savedScrolls.current[depth] || 0;
            requestAnimationFrame(() => window.scrollTo(0, y));
        };
        window.addEventListener('popstate', onPop);
        return () => window.removeEventListener('popstate', onPop);
    }, [onClose, topicId]);

    const pushEntry = (entry) => {
        const depth = stackRef.current.length - 1;
        savedScrolls.current[depth] = window.scrollY;
        window.history.pushState({ starLevel: stackRef.current.length }, '');
        setStack((prev) => [...prev, entry]);
    };
    const openChild = (ch) => {
        pushEntry({ kind: 'card', card: ch });
        requestAnimationFrame(() => window.scrollTo(0, 0));
    };
    const openFullscreen = (media) => pushEntry({ kind: 'fs', media });

    const top = stack[stack.length - 1];
    const fsMedia = top.kind === 'fs' ? top.media : null;

    const heading = stripHtml(activeCard.heading) || '(untitled)';
    const topLine = parentCard ? (stripHtml(parentCard.heading) || topicName) : topicName;

    const renderChild = (ch) => {
        const id = `${ch.card_type}-${ch.card_id}`;
        const isOpen = !!expandedChildIds[id];
        const chHeading = stripHtml(ch.heading) || '(untitled)';
        const hasImage = !!ch.feature_image_url;
        const [g1, g2] = gradientFor(ch.parent_heading || chHeading || topicName);
        return (
            <article
                key={id}
                className={`stf-card ${hasImage ? 'stf-card-image' : 'stf-card-compact'} ${isOpen ? 'stf-card-open' : ''}`}
                style={!hasImage ? { '--stf-g1': g1, '--stf-g2': g2 } : undefined}
            >
                <div
                    className="stf-card-head"
                    onClick={() => setExpandedChildIds((prev) => ({ ...prev, [id]: !prev[id] }))}
                >
                    {hasImage && (
                        <img src={resolveImageUrl(ch.feature_image_url)} alt="" className="stf-card-img" loading="lazy" />
                    )}
                    {ch.parent_heading && <span className="stf-eyebrow">{ch.parent_heading}</span>}
                    <h3 className="stf-heading">{chHeading}</h3>
                    {ch.short_summary && (
                        <p className={`stf-snippet ${isOpen ? 'stf-snippet-open' : ''}`}>{ch.short_summary}</p>
                    )}
                    <div className="stf-meta">
                        {ch.reading_time_min ? <span>{ch.reading_time_min} {t.minRead}</span> : null}
                    </div>
                </div>

                {isOpen && (
                    <div className="stf-card-more">
                        {Array.isArray(ch.key_points) && ch.key_points.length > 0 && (
                            <ul className="stf-keypoints">
                                {ch.key_points.slice(0, 3).map((p, i) => (
                                    <li key={i}>{p}</li>
                                ))}
                            </ul>
                        )}
                        <button className="stf-study-btn" onClick={() => openChild(ch)}>
                            {t.studyThis} &rarr;
                        </button>
                    </div>
                )}
            </article>
        );
    };
    
    
    return (
        <div className="star-container">
            <div className="star-header">
                <button className="star-close" onClick={() => window.history.back()} aria-label="Back">&larr;</button>
                <div className="star-header-text">
                    <span className="star-topic">{topLine}</span>
                </div>
                <button
                    className="star-imgtoggle-btn"
                    onClick={toggleImages}
                    title={hideImages ? 'Show images' : 'Hide images'}
                    aria-label={hideImages ? 'Show images' : 'Hide images'}
                >
                    {hideImages ? '🖼️' : '🚫'}
                </button>
                {askAiConf?.enabled && (
                    <button className="star-askai-btn" onClick={() => setAskAiTarget({ card: null, block: null })}>✦</button>
                )}
            </div>

            <section className="star-section">
                {activeCard.parent_heading && (
                    <span className="stf-eyebrow">{activeCard.parent_heading}</span>
                )}
                <div className="star-title-row">
                    <h2 className="star-title">{heading}</h2>
                    <FlagChip topicId={topicId} card={activeCard} language={language} initial={progressMap[activeKey]?.flag || null}/>
                </div>
                {!hideImages && activeCard.feature_image_url && !activeCard.feature_image_is_fallback && (
                    <img
                        src={resolveImageUrl(activeCard.feature_image_url)}
                        alt=""
                        className="star-feature-img"
                        onClick={() => openFullscreen({ type: 'image', url: resolveImageUrl(activeCard.feature_image_url), caption: heading })}
                    />
                )}

                {blocksError ? (
                    <p className="star-retry" onClick={retry}>{t.loadFail}</p>
                ) : blocks === null ? (
                    <div className="stf-skeleton star-skel">
                        <div className="stf-skel-line" />
                        <div className="stf-skel-line stf-skel-line-lg" />
                        <div className="stf-skel-line" />
                    </div>
                ) : (
                    mergeContent(blocks, children).map((item) => (
                        item.kind === 'block' ? (
                            <div key={`b-${item.key}`} className="star-block">
                                <ContentBlockRenderer
                                    block={item.data}
                                    onMediaClick={openFullscreen}
                                    onAskAi={askAiConf?.enabled ? (b) => setAskAiTarget({ card: activeCard, block: b }) : undefined}
                                    askAiTypes={askAiConf?.block_types}
                                />
                            </div>
                        ) : (
                            renderChild(item.data)
                        )
                    ))
                )}

                <ConfidenceStrip topicId={topicId} card={activeCard} language={language} initial={progressMap[activeKey]?.confidence || null} />
            </section>


            {askAiTarget && (
                <AskAiPanel
                    topicId={topicId}
                    topicName={topicName}
                    card={askAiTarget.card}
                    block={askAiTarget.block}
                    language={language}
                    onClose={() => setAskAiTarget(null)}
                />
            )}

            {fsMedia && (
                <FullscreenViewer media={fsMedia} onClose={() => window.history.back()} />
            )}
        </div>
    );
};

export default StudentArticleReader;